import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import isEqual from 'lodash/isEqual';

import {
  EmptyState,
  ExportButton,
  FilterButton,
  LimitPaginationButton,
  Loading,
  PageTitle,
  PaginationGroup,
  SaveFilterBlock,
  TableColumnSetting,
  TableTitle
} from 'components/common/Elements/Elements';
import InputSearch from 'components/common/forms/InputSearch/InputSearch';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';
import SatisfactionBoardTable from './parts/SatisfactionBoardTable/SatisfactionBoardTable';
import FiltersBlock from './parts/FiltersBlock/FiltersBlock';
import Widgets from './parts/Widgets/Widgets';

import {
  defaultFilters,
  defaultNavigation,
  exportConfigSatisfactionBoard,
  mainTableConf
} from './helpers/configurates';
import { getRequest, postRequest } from 'utils/requests';

import {
  replaceHistory,
  paddingForMenuSettings,
  checkingForExistenceOfOptions,
  parsConfigForHistory,
  reParsConfigForHistory,
  clearOrDefaultFilters
} from 'utils/common';
import { createReqBody } from './helpers/helpers';

import { validationNumber } from 'utils/validation';

import classes from './SatisfactionBoard.module.scss';
import { access, hasAccess, resources } from 'utils/permissions';

const SatisfactionBoard = () => {
  const [filter, setFilter] = useState({
    ...defaultFilters,
    ...defaultNavigation
  });

  const [filterBackend, setFilterBackend] = useState({
    key: 'satisFilters',
    value: []
  });

  const [visibleFilterSave, setVisibleFilterSave] = useState(false);
  const [visibleFilterEdit, setVisibleFilterEdit] = useState(false);
  const [changeFilter, setChangeFilter] = useState('Default');
  const [trigerResetDatePicker, setTrigerResetDatePicker] = useState(false);

  const [filterButton, setFilterButton] = useState(false);
  const [tableConf, setTableConf] = useState(
    JSON.parse(JSON.stringify(mainTableConf))
  );

  const [data, setData] = useState({});
  const [filters, setFilters] = useState({});

  const [loader, setLoader] = useState(true);
  const [loaderTable, setLoaderTable] = useState(false);
  const [dataConection, setDataConection] = useState(false);
  const [selectLoader, setSelectLoader] = useState(false);

  const [startFilter, setStartFilter] = useState(false);

  const [prevStateFilter, setPrevStateFilter] = useState();
  const [prevStateTableConf, setPrevStateTableConf] = useState();

  const [triggerSaveTableSettings, setTriggerSaveTableSettings] =
    useState(false);
  const [triggerStartEffect, setTriggerStartEffect] = useState(false);
  const [enabledRequest, setEnabledRequest] = useState(true);

  const { t } = useTranslation(['activity']);

  const history = useHistory();
  const query = new URLSearchParams(history.location.search);

  const tableHeight = useRef();
  const unmount = useRef(false);

  const handleFilterInput = e => {
    setFilter({ ...filter, companyName: e.target.value, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filter.companyName, value =>
    handleFilterInput({ target: { value } })
  );

  const handleFilterButton = () => {
    setFilterButton(!filterButton);
  };

  const handleLimitChange = limit => {
    if (filter.itemsPerPage === limit) {
      return;
    }
    setFilter({ ...filter, itemsPerPage: limit, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  const handleSortable = field => () => {
    if (field === filter.fieldName) {
      const directionTemp = filter.direction * -1;
      setFilter({ ...filter, direction: directionTemp });
      setStartFilter(!startFilter);
    } else {
      setFilter({ ...filter, fieldName: field });
      setStartFilter(!startFilter);
    }
  };

  const changeCurrentPage = e => {
    setFilter({ ...filter, currentPage: e });
    setStartFilter(!startFilter);
  };

  const onClearSearch = () => {
    setFilter({ ...filter, companyName: '' });
    setStartFilter(!startFilter);
  };

  const handleFromTo = e => {
    const target = e.currentTarget;
    const name = target.getAttribute('field');
    if (target.id === name) {
      setFilter({
        ...filter,
        [name]: {
          ...filter[name],
          [`from`]: '',
          [`to`]: ''
        }
      });
      setStartFilter(!startFilter);
    } else {
      if (!validationNumber(target.value)) {
        setFilter({
          ...filter,
          [name]: {
            ...filter[name],
            [target.name]: target.value
          }
        });
        setStartFilter(!startFilter);
      }
    }
  };

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  const getFilterOptionsFromBackend = listFilter => {
    setSelectLoader(true);
    getRequest('/cs/satisfaction/list/filters')
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setFilters({ ...filters, ...body });
          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body
          );
          newCheckingList && setPrevStateFilter(newCheckingList);
          newCheckingList &&
            replaceHistory(
              filter,
              setEnabledRequest,
              history,
              'validation',
              newCheckingList,
              changeFilter,
              parsConfigForHistory(tableConf)
            );
          setSelectLoader(false);
        }
      })
      .catch(err => {
        !unmount.current && console.log(err);
      });
  };

  useEffect(() => {
    if (triggerStartEffect) {
      if (
        (!isEqual(prevStateFilter, filter) &&
          JSON.parse(query.get('filter')) !== null) ||
        !isEqual(
          parsConfigForHistory(prevStateTableConf),
          parsConfigForHistory(tableConf)
        )
      ) {
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          false,
          false,
          changeFilter,
          parsConfigForHistory(tableConf)
        );
      }
    }
    setTriggerStartEffect(true);
    // eslint-disable-next-line
  }, [startFilter, tableConf]);

  useEffect(() => {
    if (enabledRequest) {
      setLoaderTable(true);
      if (
        query.get('filter') === null &&
        isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          'replace',
          false,
          'Default',
          parsConfigForHistory(tableConf)
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
        setPrevStateFilter(filter);
        setPrevStateTableConf(tableConf);
        requestForFilteredData();
      } else if (
        query.get('filter') === null &&
        !isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        resetAllfilters(true);
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          'replace',
          {
            ...defaultFilters,
            ...defaultNavigation
          },
          'Default',
          tableConf
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
      } else {
        setFilter(JSON.parse(query.get('filter')));
        setPrevStateFilter(JSON.parse(query.get('filter')));
        setPrevStateTableConf(
          reParsConfigForHistory(JSON.parse(query.get('tableConf')))
        );
        requestForFilteredData(JSON.parse(query.get('filter')));
        setChangeFilter(query.get('changeFilter'));
        setTableConf(
          reParsConfigForHistory(
            JSON.parse(query.get('tableConf')),
            mainTableConf
          )
        );
      }
      setTrigerResetDatePicker(!trigerResetDatePicker);
    }
    setEnabledRequest(true);

    // eslint-disable-next-line
  }, [query.get('filter') || query.get('jarvisRegion')]);

  const requestForFilteredData = (listFilter = filter) => {
    const linkForRequest = `/cs/satisfaction/list?page=${listFilter.currentPage}&limit=${listFilter.itemsPerPage}`;

    const REQ_BODY = createReqBody(listFilter);
    postRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!unmount.current) {
          setData(res.body);
          setDataConection(true);
          setLoader(false);
          setLoaderTable(false);
          getFilterOptionsFromBackend(listFilter);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          setDataConection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
        }
      });
  };

  const resetAllfilters = (
    reload,
    onlyFilterLabel,
    onClickDefault,
    modeClearAll
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    } else {
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    }

    setTrigerResetDatePicker(!trigerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  if (loader) {
    return <Loading />;
  }

  if (!dataConection) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      <PageTitle title={t('activity:satisfaction.SATISFACTION_BOARD')} />
      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('activity:filters.SEARCH_PLACEHOLDER_CNE')}
          value={filter.companyName}
          onChange={handleFilterInput}
          onClearSearch={onClearSearch}
          className={classes.filter}
          disabled={loaderTable}
        />
        <FilterButton active={filterButton} onClick={handleFilterButton} />
        <SaveFilterBlock
          setFilterButton={setFilterButton}
          visibleFilterSave={visibleFilterSave}
          setVisibleFilterSave={setVisibleFilterSave}
          visibleFilterEdit={visibleFilterEdit}
          setVisibleFilterEdit={setVisibleFilterEdit}
          filter={filter}
          setFilter={setFilter}
          filterBackend={filterBackend}
          setFilterBackend={setFilterBackend}
          changeFilter={changeFilter}
          setChangeFilter={setChangeFilter}
          resetAllfilters={resetAllfilters}
          defaultNavigation={defaultNavigation}
          trigerResetDatePicker={trigerResetDatePicker}
          setTrigerResetDatePicker={setTrigerResetDatePicker}
          startFilter={startFilter}
          setStartFilter={setStartFilter}
          defaultFilters={defaultFilters}
          tableConf={tableConf}
          defaulTableConf={mainTableConf}
          setTableConf={setTableConf}
          triggerSaveTableSettings={triggerSaveTableSettings}
        />
      </div>
      <FiltersBlock
        filter={filter}
        setFilter={setFilter}
        startFilter={startFilter}
        setStartFilter={setStartFilter}
        filters={filters}
        filterButton={filterButton}
        trigerResetDatePicker={trigerResetDatePicker}
        resetAllfilters={resetAllfilters}
        selectLoader={selectLoader}
        setVisibleFilterSave={setVisibleFilterSave}
        setVisibleFilterEdit={setVisibleFilterEdit}
        changeFilter={changeFilter}
      />
      <Widgets data={data.widgetData} />
      <div className={classes.actions}>
        <TableTitle
          title={t('activity:satisfaction.SATISFACTION_BOARD')}
          additional={data.totalReqCount}
        />
        <div className={classes.actions_right}>
          <TableColumnSetting
            setFilterButton={setFilterButton}
            tableConf={tableConf}
            setTableConf={setTableConf}
            setVisibleFilterSave={setVisibleFilterSave}
            setVisibleFilterEdit={setVisibleFilterEdit}
            changeFilter={changeFilter}
            visibleFilterSave={visibleFilterSave}
            visibleFilterEdit={visibleFilterEdit}
            setTriggerSaveTableSettings={setTriggerSaveTableSettings}
            triggerSaveTableSettings={triggerSaveTableSettings}
            defaulTableConf={mainTableConf}
          />
          {hasAccess([resources.SATISFACTION_BOARD, access.EXPORT]) && (
            <ExportButton
              exportConfig={exportConfigSatisfactionBoard}
              changeOptions={tableConf}
              isDisabled={!data.totalReqCount}
            />
          )}
          <LimitPaginationButton
            currentLimit={filter.itemsPerPage}
            onLimitChange={handleLimitChange}
          />
        </div>
      </div>
      <div style={paddingForMenuSettings(tableConf, tableHeight)}>
        <div ref={tableHeight}>
          <SatisfactionBoardTable
            data={data.tableData}
            handleSortable={handleSortable}
            loader={loaderTable}
            tableConf={tableConf}
            filter={filter}
            handleFromTo={handleFromTo}
          />
        </div>
        {data.tableData && loaderTable === false && data.totalPages > 1 && (
          <PaginationGroup
            pageCount={data.totalPages}
            setPage={changeCurrentPage}
            currentPage={filter.currentPage}
            itemsTotal={data.totalReqCount}
            itemsPerPage={filter.itemsPerPage}
          />
        )}
      </div>
    </>
  );
};

export default SatisfactionBoard;
